import { t } from 'i18next'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { ConfirmDialog } from 'primereact/confirmdialog'
import { DataTable } from 'primereact/datatable'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { API_COMPANIES, API_COMPANY_BY_ID } from '../../../../constants'
import { Company } from '../../../../model/company.model'
import { RequestMethod } from '../../../../model/enums/enums'
import {
  removeCompany,
  updateCompany,
} from '../../../../redux/basic/company/companyActions'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import { AuthRoutes } from '../../../../router/routes'
import { handleRequest } from '../../../../services/api/base/handleRequest'
import { companyRelationshipList } from '../../../util/form/dropdown/company-relationship-dropdown/CompanyRelationshipDropdown'

export const CompanyModule = () => {
  const dispatcher = useAppDispatch()

  const companies = useAppSelector((state) => state.companies)
  const employees = useAppSelector((state) => state.employees)
  const apprentices = useAppSelector((state) => state.apprentices)

  const [selectedCompany, setSelectedCompany] = useState<Company | undefined>()
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)
  const loadCompanies = async () => {
    const comps = await handleRequest<Company[]>(
      API_COMPANIES(),
      RequestMethod.GET
    )
    if (comps.data) {
      comps.data.forEach((c) => {
        // companies are already loaded as base info
        dispatcher(updateCompany(c))
      })
    }
  }

  useEffect(() => {
    loadCompanies()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dt = useRef<DataTable>(null)

  // const ceoBody = (rowData: Company) => {
  //     const ceo = findPersonById(rowData.id_ceo, employees)
  //     return AvatarCell(ceo)
  // }
  //
  // const appAdminBody = (rowData: Company) => {
  //     const appAdmin = findPersonById(rowData.id_app_admin, employees)
  //     return AvatarCell(appAdmin)
  // }

  const logoBody = (rowData: Company) => {
    if (
      !rowData.company_logo_reference ||
      rowData.company_logo_reference.substring(0, 5) !== 'https'
    ) {
      return <></>
    }
    return (
      <div className={'flex align-items-center justify-content-center w-full'}>
        <img
          style={{ width: 'auto', maxHeight: '32px', maxWidth: '200px' }}
          src={rowData.company_logo_reference}
          alt={''}
        />
      </div>
    )
  }

  const countEmployeesBody = (companyId: number) => {
    const numberOfEmployees = employees.filter(
      (e) => e.id_company === companyId
    )?.length
    return <span>{numberOfEmployees ? numberOfEmployees : 0}</span>
  }

  const countApprenticesBody = (companyId: number) => {
    const numberOfApprentices = apprentices.filter(
      (e) => e.id_company === companyId
    )?.length
    return <span>{numberOfApprentices ? numberOfApprentices : 0}</span>
  }

  const companyRelationshipTypeBody = (company: Company) => {
    const label = companyRelationshipList.find(
      (entry) => entry.value === company.id_company_relationship_type
    )?.label

    if (label) {
      return <span>{t<string>(label)}</span>
    }
    return <></>
  }

  const navigate = useNavigate()

  const create = () => {
    console.log(
      '[Company Module] Clicked button to create a new Company. Changing route.'
    )
    navigate(AuthRoutes.addCompany)
  }

  const edit = (company: Company) => {
    if (!company?.company_id) {
      console.warn(
        '[Company Module] Clicked on edit Company but Company id is not defined'
      )
      return
    }
    console.log(
      `[Company Module] Clicked button to edit company with id ${company.company_id}. Changing route.`
    )
    navigate(`${AuthRoutes.company}/${company.company_id}`)
  }

  const remove = async (company: Company | undefined) => {
    if (!company?.company_id) {
      console.warn('[Company Module] Company ID is somehow not defined.')
      return
    }
    //TODO check if Company is referenced and open delete dialog
    const ref = companies?.find(
      (c: Company) => c.id_company === company.company_id
    )
    const numberOfEmployees = employees.filter(
      (e) => e.id_company === company.company_id
    )?.length
    const numberOfApprentices = apprentices.filter(
      (e) => e.id_company === company.company_id
    )?.length
    if (ref) {
      console.warn(
        '[Company Module] Company is referenced by other Company and can not be deleted.'
      )
      //TODO show message
      return
    }
    if (numberOfApprentices > 0) {
      console.info(
        '[Company Module] Company is referenced by at least one apprentice and can not be deleted.'
      )
      //TODO show message
      return
    }

    if (numberOfEmployees > 0) {
      console.info(
        '[Company Module] Company is referenced by at least one employee and can not be deleted.'
      )
      //TODO show message
      return
    }

    const result = await handleRequest(
      API_COMPANY_BY_ID(company.company_id),
      RequestMethod.DELETE
    )
    if (!result.error) {
      // remove from redux store
      dispatcher(removeCompany(company.company_id))
      setSelectedCompany(undefined)
    }
  }

  const openDeleteDialog = (rowData: Company) => {
    setSelectedCompany(rowData)
    setShowDeleteDialog(true)
  }

  /**
   * Company should and can only be deleted if there is no reference set on it.
   * @param {Company} rowData
   * @returns {JSX.Element}
   */
  const companyActionBodyWithConditionalDelete = (rowData: Company) => {
    const ref = companies.find(
      (c: Company) => c.id_company === rowData.company_id
    )
    const numberOfEmployees = employees.filter(
      (e) => e.id_company === rowData.company_id
    )?.length
    const numberOfApprentices = apprentices.filter(
      (e) => e.id_company === rowData.company_id
    )?.length

    // eslint-disable-next-line react/display-name

    return (
      <>
        <Button
          type={'button'}
          icon="fa-solid fa-pen fa-lg"
          className="p-button-rounded p-button-primary p-button-text mr-2"
          tooltip={t<string>('core.edit')}
          tooltipOptions={{
            appendTo: document.body,
            position: 'left',
            showDelay: 800,
          }}
          onClick={() => edit(rowData)}
        />
        {
          // do only show delete button if no ref is known
          !ref && numberOfApprentices === 0 && numberOfEmployees === 0 ? (
            <Button
              type={'button'}
              icon={'fa-solid fa-trash'}
              className="p-button-rounded p-button-danger p-button-text"
              onClick={() => openDeleteDialog(rowData)}
            />
          ) : (
            <Button
              type={'button'}
              icon={'fas fa-info-circle'}
              tooltip={t<string>('company.has_reference')}
              tooltipOptions={{ appendTo: document.body, position: 'left' }}
              className="p-button-rounded p-button-help p-button-text"
            />
          )
        }
      </>
    )
  }

  return (
    <div className={'card w-full'}>
      <h3>{t<string>('company.companies')}</h3>
      <p className={'secondary-text small-text mt-2 mb-2'}>
        {t<string>('company.companies_explanation')}
      </p>

      <Button
        label={t<string>('company.add')}
        className={'mb-3 mt-2'}
        onClick={create}
      />

      <DataTable
        ref={dt}
        className={'p-datatable overflow-auto'}
        value={companies}
        scrollHeight={'calc(100vh - 290px)'}
        emptyMessage={t<string>('core.empty_message')}
        resizableColumns
        columnResizeMode={'expand'}
        rowHover={true}
        stripedRows={true}
      >
        {/*<Column*/}
        {/*    sortField={'company_id'}*/}
        {/*    field={'company_id'}*/}
        {/*    sortable={true}*/}
        {/*    app-header={t<string>('company.company_id')}*/}
        {/*/>*/}
        <Column field={'company_logo_reference'} body={logoBody} />
        <Column
          sortField={'company_name'}
          field={'company_name'}
          sortable={true}
          header={t<string>('company.company_name')}
        />
        <Column
          sortField={'uid'}
          field={'uid'}
          sortable={true}
          header={t<string>('company.uid')}
        />
        <Column
          sortField={'address'}
          field={'address'}
          sortable={true}
          header={t<string>('company.address')}
        />
        <Column
          field={'id_company_relationship_type'}
          header={t<string>('company.id_company_relationship_type')}
          body={companyRelationshipTypeBody}
        />

        {/*<Column*/}
        {/*    sortField={'p_o_box'}*/}
        {/*    field={'p_o_box'}*/}
        {/*    sortable={true}*/}
        {/*    app-header={t<string>('company.p_o_box')}*/}
        {/*/>*/}
        {/*<Column*/}
        {/*    sortField={'phone'}*/}
        {/*    field={'phone'}*/}
        {/*    sortable={true}*/}
        {/*    body={row => formatted-phone-number(row.phone)}*/}
        {/*    app-header={t<string>('company.phone')}*/}
        {/*/>*/}
        {/*<Column*/}
        {/*    sortField={'email'}*/}
        {/*    field={'email'}*/}
        {/*    sortable={true}*/}
        {/*    app-header={t<string>('company.email')}*/}
        {/*/>*/}
        {/*<Column*/}
        {/*    field={'id_ceo'}*/}
        {/*    body={ceoBody}*/}
        {/*    app-header={t<string>('company.id_ceo')}*/}
        {/*/>*/}
        {/*<Column*/}
        {/*    field={'id_app_admin'}*/}
        {/*    body={appAdminBody}*/}
        {/*    app-header={t<string>('company.id_app_admin')}*/}
        {/*/>*/}
        <Column
          sortable={false}
          header={t<string>('company.number_of_employees_ct')}
          body={(row) => countEmployeesBody(row.company_id)}
        />
        <Column
          sortable={false}
          body={(row) => countApprenticesBody(row.company_id)}
          header={t<string>('company.number_of_apprentices_ct')}
        />
        {/*<Column*/}
        {/*    sortField={'vacation_days'}*/}
        {/*    field={'vacation_days'}*/}
        {/*    sortable={true}*/}
        {/*    app-header={t<string>('company.vacation_days')}*/}
        {/*/>*/}
        <Column
          style={{ minWidth: '100px', maxWidth: '6rem' }}
          body={companyActionBodyWithConditionalDelete}
        />
      </DataTable>
      <ConfirmDialog
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        header={t<string>('dialog.delete.title', {
          title: selectedCompany?.company_name,
        })}
        message={t<string>('company.delete_verification')}
        icon={'pi pi-exclamation-triangle icon-orange'}
        acceptClassName={'p-button-danger'}
        accept={() => remove(selectedCompany)}
        //reject={reject}
        className={'dialog'}
        acceptLabel={t<string>('core.yes')}
        rejectLabel={t<string>('core.no')}
      />
    </div>
  )
}